/**
 * Zustand store for local UI state. Memory-only (no persist middleware in v1)
 * per ARCH §6.1. Slices:
 *   - uiSlice         capture sheet, command palette, household-switcher open
 *   - householdSlice  mirror of the active household id from /api/auth/session
 *   - captureSlice    current draft text, voice listening, current parse result
 *   - toastSlice      ephemeral toasts (bottom-center, serif italic)
 *
 * The mirror in householdSlice is reconciled on app boot from the server's
 * authoritative `/api/auth/session`. Active household persists across reloads
 * in localStorage purely as a UI hint — the server decides what's active.
 */
import { create } from 'zustand';
import type { HouseholdId } from '@shared/types';

// ---------- Toast slice ----------

export type ToastVariant = 'success' | 'error' | 'info';

export interface Toast {
  id: string;
  variant: ToastVariant;
  message: string;
  /** Optional undo action; if provided, toast renders an inline Undo link. */
  undo?: { label: string; onUndo: () => void };
  /** When undefined, success/info auto-dismiss after 3000ms. Errors are sticky. */
  autoDismissMs?: number | null;
}

// ---------- Capture slice ----------

export interface CaptureSliceState {
  /** The textarea draft in the open capture sheet. */
  draftText: string;
  /** Currently-running voice dictation. */
  voiceListening: boolean;
  /** Active capture-sheet open state. */
  sheetOpen: boolean;
  /**
   * The intent_id returned from /api/intents/parse. Lives only while the
   * confirmation card is on screen (the user hasn't applied yet).
   */
  pendingIntentId: string | null;
}

// ---------- Active household slice ----------

const LS_ACTIVE_HOUSEHOLD = 'cn:activeHouseholdHint';

function readActiveHouseholdHint(): HouseholdId | null {
  try {
    const raw = window.localStorage.getItem(LS_ACTIVE_HOUSEHOLD);
    if (!raw) return null;
    if (!raw.startsWith('hh_')) return null;
    return raw as HouseholdId;
  } catch {
    return null;
  }
}

function writeActiveHouseholdHint(value: HouseholdId | null): void {
  try {
    if (value === null) window.localStorage.removeItem(LS_ACTIVE_HOUSEHOLD);
    else window.localStorage.setItem(LS_ACTIVE_HOUSEHOLD, value);
  } catch {
    /* localStorage may be unavailable (e.g., private mode) — silent fallback. */
  }
}

export interface AppStore {
  // UI
  commandPaletteOpen: boolean;
  householdSwitcherOpen: boolean;
  shortcutsCheatsheetOpen: boolean;

  // Active household (mirror of server state, hinted from localStorage)
  activeHouseholdId: HouseholdId | null;

  // Capture
  capture: CaptureSliceState;

  // Toasts
  toasts: Toast[];

  // Actions
  openCommandPalette: () => void;
  closeCommandPalette: () => void;
  toggleCommandPalette: () => void;

  openHouseholdSwitcher: () => void;
  closeHouseholdSwitcher: () => void;

  openShortcutsCheatsheet: () => void;
  closeShortcutsCheatsheet: () => void;

  setActiveHousehold: (id: HouseholdId | null) => void;

  openCaptureSheet: () => void;
  closeCaptureSheet: () => void;
  setCaptureDraftText: (text: string) => void;
  setVoiceListening: (listening: boolean) => void;
  setPendingIntentId: (id: string | null) => void;
  resetCapture: () => void;

  pushToast: (toast: Omit<Toast, 'id'>) => string;
  dismissToast: (id: string) => void;
  clearToasts: () => void;
}

const emptyCaptureState: CaptureSliceState = {
  draftText: '',
  voiceListening: false,
  sheetOpen: false,
  pendingIntentId: null,
};

export const useAppStore = create<AppStore>((set) => ({
  commandPaletteOpen: false,
  householdSwitcherOpen: false,
  shortcutsCheatsheetOpen: false,

  activeHouseholdId:
    typeof window !== 'undefined' ? readActiveHouseholdHint() : null,

  capture: { ...emptyCaptureState },
  toasts: [],

  openCommandPalette: () => set({ commandPaletteOpen: true }),
  closeCommandPalette: () => set({ commandPaletteOpen: false }),
  toggleCommandPalette: () =>
    set((s) => ({ commandPaletteOpen: !s.commandPaletteOpen })),

  openHouseholdSwitcher: () => set({ householdSwitcherOpen: true }),
  closeHouseholdSwitcher: () => set({ householdSwitcherOpen: false }),

  openShortcutsCheatsheet: () => set({ shortcutsCheatsheetOpen: true }),
  closeShortcutsCheatsheet: () => set({ shortcutsCheatsheetOpen: false }),

  setActiveHousehold: (id) => {
    writeActiveHouseholdHint(id);
    set({ activeHouseholdId: id });
  },

  openCaptureSheet: () =>
    set((s) => ({ capture: { ...s.capture, sheetOpen: true } })),
  closeCaptureSheet: () =>
    set((s) => ({ capture: { ...s.capture, sheetOpen: false } })),
  setCaptureDraftText: (text) =>
    set((s) => ({ capture: { ...s.capture, draftText: text } })),
  setVoiceListening: (listening) =>
    set((s) => ({ capture: { ...s.capture, voiceListening: listening } })),
  setPendingIntentId: (id) =>
    set((s) => ({ capture: { ...s.capture, pendingIntentId: id } })),
  resetCapture: () => set({ capture: { ...emptyCaptureState } }),

  pushToast: (toast) => {
    const id = `t_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
    set((s) => ({ toasts: [...s.toasts, { ...toast, id }] }));
    return id;
  },
  dismissToast: (id) =>
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  clearToasts: () => set({ toasts: [] }),
}));

// Convenience selectors — keep React renders narrow.
export const selectActiveHouseholdId = (s: AppStore) => s.activeHouseholdId;
export const selectCaptureOpen = (s: AppStore) => s.capture.sheetOpen;
export const selectToasts = (s: AppStore) => s.toasts;

export function useToast() {
  const pushToast = useAppStore((s) => s.pushToast);
  return {
    success: (message: string, opts?: Partial<Toast>) =>
      pushToast({ variant: 'success', message, ...(opts ?? {}) }),
    error: (message: string, opts?: Partial<Toast>) =>
      pushToast({ variant: 'error', message, autoDismissMs: null, ...(opts ?? {}) }),
    info: (message: string, opts?: Partial<Toast>) =>
      pushToast({ variant: 'info', message, ...(opts ?? {}) }),
  };
}

// Re-export getter for the active household hint, used by app-boot reconciliation.
export const { getState, setState } = useAppStore;

// Direct re-exports of the persistence helpers (for tests).
export { readActiveHouseholdHint, writeActiveHouseholdHint };
